import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { Typography } from '@mui/material';
import { DsrcApprovalDecision } from './options';
import { PAGE_TEXT } from '../../lang';
import { ApplicantParticulars } from '../../DRCAssessment';
import { AssessmentSidebar, ASSESSMENT_MODULE } from '../../components';
import {
  useGetDsrcApproval,
  useSubmitDrcApproval,
  useUpdateDrcApproval,
} from '../../graphql';
import { getApplicationStatus } from '../../../Nomination/graphql';
import {
  type SidebarData,
  Button,
  Alert,
  type AlertDetails,
  Loading,
  Dialog,
  type DialogDetails,
  UserRole,
} from '../../../../components';
import { DRCApprovalContent } from './DRCApprovalContent';
import { APPLICATION_STATUS_TYPE } from '../../../../utils';

export const DRCApprovalDetail: React.FunctionComponent = () => {
  const { applicationId = '' } = useParams();
  const navigate = useNavigate();
  const [openAlert, setOpenAlert] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDetails, setDialogDetails] = useState<DialogDetails>({
    title: '',
    content: '',
  });
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });

  const {
    applicationStatus,
    remarks: returnRemarks,
    loading: applicationStatusLoading,
    error: applicationStatusError,
  } = getApplicationStatus(applicationId);

  // if the application is not being assessed by dsrc, redirect to monitor
  if (
    !applicationStatusLoading &&
    applicationStatus !== APPLICATION_STATUS_TYPE.UNDER_DSRC
  ) {
    navigate(`/internal/monitor/${applicationId}`);
  }

  const {
    dsrcApprovalCreated,
    dsrcApproval,
    drcRequired,
    loading: dsrcApprovalLoading,
    error: dsrcApprovalError,
    refetch,
  } = useGetDsrcApproval(applicationId);

  const submitDrcApproval = useSubmitDrcApproval();
  const updateDrcApproval = useUpdateDrcApproval();
  const confirmSubmitDrcApproval = async (): Promise<void> => {
    setDialogOpen(false);
    try {
      if (!drcRequired) {
        // reset the previous drc approval to NA, if dsrc approval is not required any more
        const variables = {
          input: {
            applicationId,
            approveDoRPgSInTeam: DsrcApprovalDecision.NA,
            remarks: '',
          },
        };
        await updateDrcApproval({ variables });
      }
      await submitDrcApproval({
        variables: { applicationId },
      });
      setDialogDetails(dialogMappings[PAGE_TEXT.CLOSE]);
      setDialogOpen(true);
    } catch (error) {
      setAlertDetails({
        alertContent: `Error Occurs.` + (error as Error)?.message,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  const handleBackTolist = (): void => {
    window.location.href = `/internal/drc-homepage`;
  };

  const handleSubmitAssessment = async (): Promise<void> => {
    if (drcRequired) {
      setDialogDetails(dialogMappings[PAGE_TEXT.SUBMIT]);
      setDialogOpen(true);
    } else {
      // if dsrc approval is not required this time, then the dialog would not pop up
      await confirmSubmitDrcApproval();
    }
  };

  const handleCloseDialog = (): void => {
    setDialogOpen(false);
  };

  const [activatedModuleId, setActivatedModuleId] = useState(
    ASSESSMENT_MODULE.DSRC_ASSESSMENT,
  );

  const handleChangeActivated = (activatedId: string): void => {
    setActivatedModuleId(activatedId as ASSESSMENT_MODULE);
  };

  const sidebarData: SidebarData[] = [
    {
      id: ASSESSMENT_MODULE.PARTICULARS,
      name: PAGE_TEXT.DRC_ASSESSMENT_DETAILS.PARTICULARS.TITLE,
    },
    {
      id: ASSESSMENT_MODULE.DSRC_ASSESSMENT,
      name: PAGE_TEXT.DSRC_APPROVAL_DETAILS.ASSESSMENT.TITLE,
    },
  ];

  const assessmentModulesMap: Partial<
    Record<ASSESSMENT_MODULE, { component: any; moduleTitle: string }>
  > = {
    [ASSESSMENT_MODULE.PARTICULARS]: {
      component: <ApplicantParticulars showGoDetail />,
      moduleTitle: PAGE_TEXT.DRC_ASSESSMENT_DETAILS.PARTICULARS.TITLE,
    },
    [ASSESSMENT_MODULE.DSRC_ASSESSMENT]: {
      component: (
        <DRCApprovalContent
          hasDsrcApprovalCreated={dsrcApprovalCreated}
          dsrcApproval={dsrcApproval}
          drcRequired={drcRequired}
          submitDsrcApproval={handleSubmitAssessment}
          refetchAssessment={refetch}
        />
      ),
      moduleTitle: PAGE_TEXT.DSRC_APPROVAL_DETAILS.ASSESSMENT.TITLE,
    },
  };

  const activatedModule:
    | { component: React.ReactNode; moduleTitle: string }
    | undefined = assessmentModulesMap[activatedModuleId];

  const moduleComponent = activatedModule?.component ?? activatedModuleId;
  const moduleTitle = activatedModule?.moduleTitle ?? activatedModuleId;

  if (dsrcApprovalError ?? applicationStatusError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Error Occurs.` +
            (dsrcApprovalError as Error).message +
            (applicationStatusError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const loading = applicationStatusLoading || dsrcApprovalLoading;

  const drcDisapprove = Boolean(dsrcApproval.remarks);

  // submit contentText
  const { SUBMIT_SUCCESS, SUBMIT_CONFIRM } = PAGE_TEXT.DSRC_APPROVAL_DETAILS;

  const dialogMappings: Record<string, DialogDetails> = {
    [PAGE_TEXT.SUBMIT]: {
      title: SUBMIT_CONFIRM.TITLE,
      content:
        drcDisapprove && drcRequired
          ? SUBMIT_CONFIRM.CONTENT_RET
          : SUBMIT_CONFIRM.CONTENT,
      confirmAction: PAGE_TEXT.SUBMIT,
      cancelAction: PAGE_TEXT.CANCEL,
      handleDialogConfirmAction: confirmSubmitDrcApproval,
    },

    // on submit successfully
    [PAGE_TEXT.CLOSE]: {
      title: '',
      content: SUBMIT_SUCCESS,
      confirmAction: PAGE_TEXT.BACK,
      handleDialogConfirmAction: handleBackTolist,
    },

    // on open when it is a returned assessment
    [PAGE_TEXT.DSRC_APPROVAL_DETAILS.PREVIEW_DIALOG.TITLE]: {
      title: PAGE_TEXT.DSRC_APPROVAL_DETAILS.PREVIEW_DIALOG.TITLE,
      content: PAGE_TEXT.DSRC_APPROVAL_DETAILS.PREVIEW_DIALOG.CONTENT,
      confirmAction: PAGE_TEXT.OK,
      handleDialogConfirmAction: handleCloseDialog,
    },
  };

  useEffect(() => {
    if (drcRequired) {
      const frcRelatedRemark = returnRemarks.filter(
        rmk =>
          rmk.fromRole === UserRole.DRC || rmk.toRole.includes(UserRole.DRC),
      );

      if (frcRelatedRemark.length && !loading) {
        setDialogDetails(
          dialogMappings[PAGE_TEXT.DSRC_APPROVAL_DETAILS.PREVIEW_DIALOG.TITLE],
        );
        setDialogOpen(true);
      }
    }
  }, [loading]);

  return (
    <>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <Dialog
        openDialog={dialogOpen}
        width={'700px'}
        dialogTitle={dialogDetails.title}
        dialogAction={dialogDetails.confirmAction}
        cancelAction={dialogDetails.cancelAction ?? undefined}
        onDialogAction={dialogDetails.handleDialogConfirmAction}
        onDialogClose={handleCloseDialog}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {dialogDetails.content}
        </Typography>
      </Dialog>
      {loading ? (
        <Loading />
      ) : (
        <PageContainer>
          <div className="sidebar-area">
            <AssessmentSidebar
              onChangeActivated={handleChangeActivated}
              collapsible={true}
              sidebarData={sidebarData}
              drawerWidth={244}
              currentActivatedModule={activatedModuleId}
              drawerInnerWidth="15rem"
              buttonField={
                <div className="flex pt-12 justify-center w-full">
                  <Button variant="outlined" onClick={handleBackTolist}>
                    {PAGE_TEXT.BACK}
                  </Button>
                </div>
              }
            />
          </div>
          <Container>
            <h1>{moduleTitle}</h1>
            <div className="form-area">{moduleComponent}</div>
          </Container>
        </PageContainer>
      )}
    </>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: row;

  .sidebar-area {
    width: min-content;
  }

  h1 {
    margin-top: 2rem;
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    text-align: center;
    color: #951d1d;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;

  .form-area {
    flex: 0.95;
    margin: 1rem;
  }
`;
